import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, shareReplay, tap, throttleTime } from 'rxjs/operators';
import { SnackBarService } from '../shared/snackbar.service';
import { LoHiTrailer, LoHiTruck } from '../types';
import { isHttpErrorResponse } from '../utilities/isHttpErrorResponse';

@Injectable({
  providedIn: 'root',
})
export class AssetsService {
  private trucks$$ = new BehaviorSubject<LoHiTruck[]>(null);
  private trucksThrottle$$ = new Subject();
  private trucksShared$ = this.trucks$$.asObservable().pipe(
    map((trucks) => trucks || []),
    shareReplay(1),
  );

  public get trucks$(): Observable<LoHiTruck[]> {
    this.trucksThrottle$$.next();
    return this.trucksShared$;
  }

  private trailers$$ = new BehaviorSubject<LoHiTrailer[]>(null);
  private trailersThrottle$$ = new Subject();
  private trailersShared$ = this.trailers$$.asObservable().pipe(
    map((trailers) => trailers || []),
    shareReplay(1),
  );

  public get trailers$(): Observable<LoHiTrailer[]> {
    this.trailersThrottle$$.next();
    return this.trailersShared$;
  }

  constructor(private httpClient: HttpClient, private logger: NGXLogger, private snackBar: SnackBarService) {
    this.trucksThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadTrucks();
    });

    this.trailersThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadTrailers();
    });
  }

  public async addTruck(truckName: string): Promise<string> {
    try {
      const response = await this.httpClient
        .post<{ truckId: string }>(`${environment.api}/v1/truck`, { truckName: truckName.toUpperCase() })
        .toPromise();
      await this.loadTrucks();
      return response.truckId;
    } catch (error) {
      this.logger.error(error);
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error.statusText);
      } else {
        this.snackBar.showError('Failed to add truck');
      }
    }
  }

  public async addTrailer(trailerName: string): Promise<string> {
    try {
      const response = await this.httpClient
        .post<{ trailerId: string }>(`${environment.api}/v1/trailer`, { trailerName: trailerName.toUpperCase() })
        .toPromise();
      await this.loadTrailers();
      return response.trailerId;
    } catch (error) {
      this.logger.error(error);
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error.statusText);
      } else {
        this.snackBar.showError('Failed to add truck');
      }
    }
  }

  private async loadTrucks() {
    try {
      const response = await this.httpClient
        .get<{ trucks: LoHiTruck[] }>(`${environment.api}/v1/all_trucks`)
        .toPromise();
      this.trucks$$.next(response.trucks);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadTrailers() {
    try {
      const response = await this.httpClient
        .get<{ trailers: LoHiTrailer[] }>(`${environment.api}/v1/all_trailers`)
        .toPromise();
      this.trailers$$.next(response.trailers);
    } catch (error) {
      this.logger.error(error);
    }
  }

  public setDriverTruck$(id: string, value: any): Observable<any> {
    return this.httpClient
      .post(`${environment.api}/v1/admin/truck/${id}`, {
        truckID: value,
      })
      .pipe(tap(() => this.loadTrucks()));
  }

  public async putTruckMaintenanceEvent(id: string, description: string, notOperational: boolean) {
    try {
      await this.httpClient
        .put(`${environment.api}/v1/truck/${id}/maintenance_event`, {
          truckID: id,
          description,
          notOperational,
        })
        .toPromise();
      this.loadTrucks();
      return true;
    } catch (e) {
      this.logger.error(e);
      return false;
    }
  }

  public async putTrailerMaintenanceEvent(id: string, description: string, notOperational: boolean) {
    try {
      await this.httpClient
        .put(`${environment.api}/v1/trailer/${id}/maintenance_event`, {
          trailerID: id,
          description,
          notOperational,
        })
        .toPromise();
      this.loadTrailers();
      return true;
    } catch (e) {
      this.logger.error(e);
      return false;
    }
  }

  public async editTruck(id: string, name: string, operational: boolean, owner: string | null, lessee: string | null) {
    try {
      await this.httpClient
        .put(`${environment.api}/v1/truck/${id}/edit`, {
          name,
          operational,
          truckOwner: owner,
          truckLessee: lessee,
        })
        .toPromise();
      this.loadTrucks();
      return true;
    } catch (e) {
      this.logger.error(e);
      return false;
    }
  }

  public async editTrailer(
    id: string,
    name: string,
    operational: boolean,
    owner: string | null,
    lessee: string | null,
  ) {
    try {
      await this.httpClient
        .put(`${environment.api}/v1/trailer/${id}/edit`, {
          name,
          operational,
          trailerOwner: owner,
          trailerLessee: lessee,
        })
        .toPromise();
      this.loadTrailers();
      return true;
    } catch (e) {
      this.logger.error(e);
      return false;
    }
  }

  public async archiveTruck(id: string) {
    try {
      await this.httpClient.delete(`${environment.api}/v1/truck/${id}`).toPromise();
      this.loadTrucks();
      return true;
    } catch (e) {
      this.logger.error(e);
      return false;
    }
  }

  public async archiveTrailer(id: string) {
    try {
      await this.httpClient.delete(`${environment.api}/v1/trailer/${id}`).toPromise();
      this.loadTrailers();
      return true;
    } catch (e) {
      this.logger.error(e);
      return false;
    }
  }
}
